import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

if (!customElements.get('tg-layerx-base-selector')) {
  customElements.define('tg-layerx-base-selector', class extends HTMLElement {
    #controller = new AbortController();
    #busy = false;
    #currentSize = null;
    #moneyFormatter;
    #priceDivisor;

    connectedCallback() {
      this.#controller = new AbortController();
      this.#moneyFormatter = new Intl.NumberFormat(this.dataset.locale, {
        style: 'currency', currency: this.dataset.currency,
      });
      const decimals = this.#moneyFormatter.resolvedOptions().maximumFractionDigits;
      this.#priceDivisor = 10 ** (decimals === 0 ? 2 : decimals);
      this.#fieldset.addEventListener('change', (event) => {
        if (!event.target.matches('input[type="radio"]')) return;
        this.#status.textContent = '';
        this.#updateSelection();
        this.#emit('select');
      }, { signal: this.#controller.signal });
      this.#button.addEventListener('click', () => this.#addSet(), { signal: this.#controller.signal });
      this.#other.addEventListener('toggle', () => {
        if (this.#other.open) {
          this.#emit('other_open');
        } else {
          const selected = this.#selectedInput();
          if (selected?.closest('[data-group="opf"]')) {
            selected.checked = false;
            this.#updateSelection();
          }
        }
      }, { signal: this.#controller.signal });
      this.closest('.shopify-section')?.addEventListener(StandardEvents.productSelect, async (event) => {
        try {
          await event.promise;
        } catch {
          this.#sync();
          return;
        }
        this.#sync();
      }, { signal: this.#controller.signal });
      this.#form?.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
        event.promise.then(({ detail }) => {
          if (detail?.source === 'product-form-component' && detail.productId === this.dataset.productId && !detail.didError) {
            this.#emit('plus_only_add_success', null);
          }
        }).catch(() => {});
      }, { signal: this.#controller.signal });
      window.addEventListener('pageshow', () => this.#sync(), { signal: this.#controller.signal });
      this.#sync();
      this.hidden = false;
    }

    disconnectedCallback() {
      this.#controller.abort();
    }

    get #form() { return this.closest('product-form-component'); }
    get #fieldset() { return this.querySelector('fieldset'); }
    get #button() { return this.querySelector('[data-add-set]'); }
    get #other() { return this.querySelector('[data-other]'); }
    get #status() { return this.querySelector('[data-status]'); }
    get #rows() { return [...this.querySelectorAll('[data-base-option]')]; }

    #plusVariant() {
      const id = this.#form?.querySelector('form input[name="id"]')?.value;
      return [...this.querySelectorAll('.tg-layerx-base-selector__plus-variants [data-id]')]
        .find((variant) => variant.dataset.id === id);
    }

    #selectedInput() {
      return this.#fieldset.querySelector('input[type="radio"]:checked');
    }

    #selectedRow() {
      return this.#selectedInput()?.closest('[data-base-option]');
    }

    #variantForSize(row, size) {
      return [...row.querySelectorAll('.tg-layerx-base-selector__variants [data-id]')]
        .find((variant) => variant.dataset.size === size);
    }

    #money(amount) {
      return this.#moneyFormatter.format(amount / this.#priceDivisor);
    }

    #sync() {
      const plus = this.#plusVariant();
      const size = plus?.dataset.size;
      const priorSelection = this.#selectedInput();
      if (this.#currentSize !== null && this.#currentSize !== size && priorSelection) {
        priorSelection.checked = false;
        this.#status.textContent = this.dataset.reset;
      }
      this.#currentSize = size;
      let opfCount = 0;
      let availableCount = 0;
      for (const row of this.#rows) {
        const variant = size ? this.#variantForSize(row, size) : null;
        const available = variant?.dataset.available === 'true';
        const input = row.querySelector('input[type="radio"]');
        const isOpf = row.dataset.group === 'opf';
        row.hidden = !variant || (isOpf && !available);
        if (isOpf && available) opfCount += 1;
        if (available) availableCount += 1;
        input.value = variant?.dataset.id || '';
        input.disabled = !available || plus?.dataset.available !== 'true';
        row.querySelector('[data-stock]').hidden = available || !variant;
        if (!variant) continue;
        const price = Number(variant.dataset.price);
        const rate = this.dataset.offerEnabled === 'true' && !isOpf
          ? Number(row.dataset.discountRate || 0) : 0;
        const discounted = rate ? Math.round(price * (100 - rate) / 100) : price;
        const regular = row.querySelector('[data-regular]');
        const badge = row.querySelector('[data-badge]');
        regular.hidden = !rate;
        if (rate) regular.textContent = this.#money(price);
        if (badge) badge.hidden = !rate;
        row.querySelector('[data-price]').textContent = this.#money(discounted);
      }
      this.#other.hidden = opfCount === 0;
      if (!opfCount) this.#other.open = false;
      const noOptions = this.querySelector('[data-no-options]');
      if (noOptions) noOptions.hidden = !size || availableCount > 0;
      const selected = this.#selectedInput();
      if (selected && (selected.disabled || selected.closest('[data-base-option]').hidden)) {
        selected.checked = false;
        this.#status.textContent = this.dataset.reset;
      }
      this.#updateSelection();
    }

    #updateSelection() {
      const plus = this.#plusVariant();
      const row = this.#selectedRow();
      const input = row?.querySelector('input[type="radio"]');
      const base = row && plus ? this.#variantForSize(row, plus.dataset.size) : null;
      const valid = plus?.dataset.available === 'true' && input && !input.disabled
        && base?.dataset.available === 'true' && input.value === base.dataset.id;
      this.#button.disabled = this.#busy || !valid;
      this.#button.textContent = valid ? this.dataset.add : this.dataset.choose;
      if (!valid) {
        this.querySelector('[data-total]').textContent = '—';
        return;
      }
      const rate = this.dataset.offerEnabled === 'true' && row.dataset.group === 'featured'
        ? Number(row.dataset.discountRate || 0) : 0;
      const basePrice = Number(base.dataset.price);
      const total = Number(plus.dataset.price) + Math.round(basePrice * (100 - rate) / 100);
      this.querySelector('[data-total]').textContent = this.#money(total);
    }

    #emit(action, base = this.#selectedInput()?.value || null) {
      this.dispatchEvent(new CustomEvent('tg:layerx-base-selector', {
        bubbles: true,
        detail: { action, plusVariantId: this.#plusVariant()?.dataset.id || null, baseVariantId: base },
      }));
    }

    async #addSet() {
      if (this.#busy) return;
      const plus = this.#plusVariant();
      const row = this.#selectedRow();
      const base = row && plus ? this.#variantForSize(row, plus.dataset.size) : null;
      if (!plus || plus.dataset.available !== 'true' || !base || base.dataset.available !== 'true'
        || this.#selectedInput()?.value !== base.dataset.id) {
        this.#status.textContent = this.dataset.sizeError;
        return;
      }
      this.#busy = true;
      this.#button.disabled = true;
      this.#status.textContent = '';
      const root = window.Shopify?.routes?.root || '/';
      const items = [
        { id: Number(plus.dataset.id), quantity: 1 },
        { id: Number(base.dataset.id), quantity: 1 },
      ];
      const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
        .map((element) => element.dataset.sectionId).filter(Boolean);
      const deferred = CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: 1 })),
        promise: deferred.promise,
      }));
      try {
        const response = await fetch(`${root}cart/add.js`, {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items, sections: sectionIds }),
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.error);
        const cartResponse = await fetch(`${root}cart.js`, { credentials: 'same-origin' });
        if (!cartResponse.ok) throw new Error(this.dataset.error);
        const cart = await cartResponse.json();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { items: cart.items, sections: result.sections, source: 'tg-layerx-base-selector', didError: false },
        });
        this.#emit('add_success');
      } catch (error) {
        deferred.reject(error);
        this.#status.textContent = error.message || this.dataset.error;
        this.#emit('add_failure');
      } finally {
        this.#busy = false;
        this.#sync();
      }
    }
  });
}
